import { useState } from 'react';
import useSWR from 'swr';
import { adminApi, type AdminTarget } from '../../api/admin';

const EMPTY_SLACK: AdminTarget = { id: '', type: 'slack', webhook_env: '' };
const EMPTY_EMAIL: AdminTarget = { id: '', type: 'email', to: [''], from: '' };

export function TargetsTab() {
  const { data: targets = [], mutate } = useSWR('admin/targets', () => adminApi.listTargets());
  const [editing, setEditing] = useState<AdminTarget | null>(null);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; ok: boolean; msg: string } | null>(
    null,
  );

  async function sendTest(t: AdminTarget) {
    setErr(null);
    setTesting(t.id);
    setTestResult(null);
    try {
      const res = await adminApi.testTarget(t.id);
      const msg = res.ok
        ? t.type === 'slack'
          ? `sent via ${res.via ?? 'slack'}`
          : `sent to ${(res.to ?? []).join(', ')}`
        : (res.error ?? 'delivery failed');
      setTestResult({ id: t.id, ok: res.ok, msg });
    } catch (e) {
      setTestResult({ id: t.id, ok: false, msg: e instanceof Error ? e.message : String(e) });
    } finally {
      setTesting(null);
    }
  }

  async function save(t: AdminTarget) {
    setErr(null);
    try {
      if (adding) await adminApi.createTarget(t);
      else await adminApi.updateTarget(t);
      await mutate();
      setEditing(null);
      setAdding(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function remove(t: AdminTarget) {
    if (!confirm(`Delete target '${t.id}'?`)) return;
    setErr(null);
    try {
      await adminApi.deleteTarget(t.id);
      await mutate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-cockpit-dim">
          Notification targets ({targets.length})
        </h3>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded border border-cockpit-border px-3 py-1 text-xs hover:bg-cockpit-muted"
            onClick={() => {
              setAdding(true);
              setEditing({ ...EMPTY_SLACK });
            }}
          >
            + Slack target
          </button>
          <button
            type="button"
            className="rounded border border-cockpit-border px-3 py-1 text-xs hover:bg-cockpit-muted"
            onClick={() => {
              setAdding(true);
              setEditing({ ...EMPTY_EMAIL });
            }}
          >
            + Email target
          </button>
        </div>
      </div>
      {err && (
        <div className="rounded border border-cockpit-crit bg-cockpit-crit/10 p-2 text-xs text-cockpit-crit">
          {err}
        </div>
      )}
      {testResult && (
        <div
          className={
            testResult.ok
              ? 'rounded border border-cockpit-ok bg-cockpit-ok/10 p-2 text-xs text-cockpit-ok'
              : 'rounded border border-cockpit-crit bg-cockpit-crit/10 p-2 text-xs text-cockpit-crit'
          }
        >
          test → <code className="font-mono">{testResult.id}</code>: {testResult.msg}
        </div>
      )}

      <div className="overflow-hidden rounded border border-cockpit-border">
        <table className="w-full text-sm">
          <thead className="bg-cockpit-panel text-xs uppercase tracking-wider text-cockpit-dim">
            <tr>
              <th className="px-3 py-2 text-left">ID</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Config</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {targets.map((t) => (
              <tr key={t.id} className="border-t border-cockpit-border">
                <td className="px-3 py-2 font-mono text-xs">{t.id}</td>
                <td className="px-3 py-2 text-xs uppercase">{t.type}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  {t.type === 'slack'
                    ? `env: ${t.webhook_env}`
                    : `${t.to.join(', ')} from ${t.from}`}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    disabled={testing === t.id}
                    className="mr-2 text-xs text-cockpit-text hover:underline disabled:opacity-40"
                    onClick={() => void sendTest(t)}
                  >
                    {testing === t.id ? 'Sending…' : 'Send test'}
                  </button>
                  <button
                    type="button"
                    className="mr-2 text-xs text-cockpit-dim hover:text-cockpit-text"
                    onClick={() => {
                      setAdding(false);
                      setEditing(t);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="text-xs text-cockpit-crit hover:underline"
                    onClick={() => void remove(t)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {targets.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-sm text-cockpit-dim">
                  no targets yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <TargetForm
          target={editing}
          isNew={adding}
          onSave={save}
          onCancel={() => {
            setEditing(null);
            setAdding(false);
          }}
        />
      )}
    </div>
  );
}

function TargetForm({
  target,
  isNew,
  onSave,
  onCancel,
}: {
  target: AdminTarget;
  isNew: boolean;
  onSave: (t: AdminTarget) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState(target);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
      className="space-y-3 rounded border border-cockpit-border bg-cockpit-panel p-4"
    >
      <h4 className="text-sm font-semibold">
        {isNew ? `New ${form.type} target` : `Edit ${target.id}`}
      </h4>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <label className="flex flex-col gap-1">
          <span className="text-cockpit-dim">ID (immutable)</span>
          <input
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 font-mono text-cockpit-text"
            value={form.id}
            disabled={!isNew}
            onChange={(e) => setForm({ ...form, id: e.target.value })}
            required
          />
        </label>
        {form.type === 'slack' ? (
          <label className="flex flex-col gap-1">
            <span className="text-cockpit-dim">webhook env-var name</span>
            <input
              className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 font-mono text-cockpit-text"
              value={form.webhook_env}
              onChange={(e) => setForm({ ...form, webhook_env: e.target.value })}
              placeholder="SLACK_OPS_WEBHOOK"
              required
            />
            <span className="text-cockpit-dim">
              The actual webhook URL is set in the Secrets tab.
            </span>
          </label>
        ) : (
          <>
            <label className="col-span-2 flex flex-col gap-1">
              <span className="text-cockpit-dim">to (one email per line)</span>
              <textarea
                className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
                rows={3}
                value={form.to.join('\n')}
                onChange={(e) =>
                  setForm({
                    ...form,
                    to: e.target.value
                      .split(/\n/)
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
                required
              />
            </label>
            <label className="col-span-2 flex flex-col gap-1">
              <span className="text-cockpit-dim">from</span>
              <input
                className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
                type="email"
                value={form.from}
                onChange={(e) => setForm({ ...form, from: e.target.value })}
                placeholder="pulse@insightdelivered.com"
                required
              />
            </label>
          </>
        )}
      </div>
      <div className="flex gap-2">
        <button type="submit" className="rounded bg-cockpit-text px-3 py-1 text-xs text-cockpit-bg">
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-cockpit-border px-3 py-1 text-xs text-cockpit-dim hover:text-cockpit-text"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
